/**
 * Pure period math for the Dashboard page (E2.1 design spec 4.1/4.2, and the
 * round-3 owner fix in 11.1/11.2): default period (current month), the
 * "Whole year" switch, the previous-period calculation the delta chips
 * compare against (rolls over the year boundary in month mode; previous
 * CALENDAR year in whole-year mode), and the month-to-date comparison
 * context that replaced the "sea of red" full-month-vs-half-month bug.
 * Kept pure so the rollover/MTD logic is unit-testable without rendering
 * anything.
 */

const MONTH_ABBR = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export interface Period {
  year: number;
  /** null means "whole year". */
  month: number | null;
}

/** Today's default period: the current month. */
export function defaultPeriod(now: Date = new Date()): Period {
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

/** The period immediately before `period` -- previous month (rolling the
 *  year back at January) in month mode, or the previous calendar year in
 *  whole-year mode. */
export function previousPeriod(period: Readonly<Period>): Period {
  if (period.month === null) {
    return { year: period.year - 1, month: null };
  }
  if (period.month === 1) {
    return { year: period.year - 1, month: 12 };
  }
  return { year: period.year, month: period.month - 1 };
}

export interface ComparisonContext {
  /** Visible chip label, e.g. "vs Jun", "vs Jun 1-13", "vs 2025 (full year)".
   *  Empty when `comparable` is false (no chip renders). */
  label: string;
  /** The tooltip's lead sentence, ahead of DeltaChip's colouring rule.
   *  Full-period: "Compared with Jun." Month-to-date, naming both capped
   *  spans: "Jul 1-13 compared with Jun 1-13 — complete days only; the
   *  figure above is month to date." Empty when `comparable` is false. */
  tooltipText: string;
  /** The to_day param for the day-capped comparison fetches (previous
   *  summary + revenue AND the capped current-side chip basis); null when
   *  the comparison is full-period vs full-period, or when there is no
   *  comparable window at all. */
  toDay: number | null;
  /** True when `period` itself is the current calendar month (drives the
   *  page title's "(month to date)" suffix). */
  isMtd: boolean;
  /** False only on the 1st of the current month: no complete day exists yet,
   *  so there is nothing to compare -- callers skip the previous-period
   *  fetches and render no chips (headline figures still show as MTD). */
  comparable: boolean;
}

/** "Jun 1-13", or "Jun 1" for a single-day span. */
function daySpanText(monthName: string, lastDay: number): string {
  return lastDay === 1 ? `${monthName} 1` : `${monthName} 1-${lastDay}`;
}

/**
 * The root cause of the "sea of red" (E2.1 spec 11.1): comparing a
 * half-elapsed current month against a full previous month guarantees a
 * ~-50% swing on every card. Fix: when `period` IS the current calendar
 * month, cap the comparison to the same day-of-month span on BOTH sides and
 * label it honestly ("vs Jun 1-13") instead of "vs Jun".
 *
 * Owner ruling 2026-10-03 (R2): the span runs through YESTERDAY, the last
 * complete day -- today's report never exists yet, so a window through
 * today would compare one day fewer on the current side. On the 1st there
 * is no complete day: nothing is comparable and no chips render. Both
 * sides are capped (the current side by its own to_day fetch; the headline
 * figures stay uncapped MTD) so an evening viewer never compares a
 * today-inclusive window against a yesterday-capped one. Revenue is capped
 * the same way (R1a), with the device licence fee flat on both sides. The
 * server clamps to_day to the shorter month, and the label does too.
 *
 * Past months keep full-month vs full-month (no to_day). Whole-year mode
 * never sends to_day (spec 11.5 names it "month+year" only) -- current-year
 * vs previous-year is labeled "(full year)" instead of suppressed: the label
 * is honest about what the PREVIOUS period covers, not a claim that the
 * current period is also complete, so it doesn't mislead (implementer's
 * call, spec 11.1 leaves this open).
 */
export function computeComparisonContext(period: Readonly<Period>, now: Date = new Date()): ComparisonContext {
  const prev = previousPeriod(period);

  if (period.month === null) {
    const periodText = `${prev.year} (full year)`;
    return { label: `vs ${periodText}`, tooltipText: `Compared with ${periodText}.`, toDay: null, isMtd: false, comparable: true };
  }

  const isMtd = period.year === now.getFullYear() && period.month === now.getMonth() + 1;
  const prevMonthName = MONTH_ABBR[prev.month === null ? 0 : prev.month - 1] ?? '';

  if (isMtd) {
    const toDay = now.getDate() - 1;
    if (toDay === 0) {
      return { label: '', tooltipText: '', toDay: null, isMtd: true, comparable: false };
    }
    const prevMonthDays = new Date(Date.UTC(prev.year, prev.month ?? 12, 0)).getUTCDate();
    const prevSpan = daySpanText(prevMonthName, Math.min(toDay, prevMonthDays));
    const currentSpan = daySpanText(MONTH_ABBR[period.month - 1] ?? '', toDay);
    return {
      label: `vs ${prevSpan}`,
      tooltipText: `${currentSpan} compared with ${prevSpan} — complete days only; the figure above is month to date.`,
      toDay,
      isMtd: true,
      comparable: true,
    };
  }

  return { label: `vs ${prevMonthName}`, tooltipText: `Compared with ${prevMonthName}.`, toDay: null, isMtd: false, comparable: true };
}
